import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProcedureRunDataEntryMode} from 'studies/StudyDAO';
import MultiBatchButton, {
  MultiBatchButtonProps,
} from 'studies/typed/process-development/multi-batch-selector/MultiBatchButton';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {NEAR_BLACK} from 'ui-library/Colors';

export interface MultiBatchSelectorProps {
  dataEntryMode: ProcedureRunDataEntryMode;
  onSelectDataEntryMode: (mode: ProcedureRunDataEntryMode) => void;
}

export default function MultiBatchSelector({
  dataEntryMode: selectedDataEntryMode,
  onSelectDataEntryMode,
}: MultiBatchSelectorProps): JSX.Element {
  return (
    <div>
      <div className={css(styles.title)}>Select a data entry mode</div>
      <div className={css(styles.buttonContainer)}>
        {Object.entries(MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE).map(
          ([mode, {graphic, headerText, description, dataTestAttrs}]) => (
            <MultiBatchButton
              key={mode}
              graphic={graphic}
              headerText={headerText}
              description={description}
              active={mode === selectedDataEntryMode}
              onSelect={() => {
                onSelectDataEntryMode(mode as ProcedureRunDataEntryMode);
              }}
              isDisabled={false}
              dataTestAttrs={dataTestAttrs}
              includeRadioButton={true}
            />
          )
        )}
      </div>
    </div>
  );
}

type DataEntryModeConfig = Omit<MultiBatchButtonProps, 'active' | 'onSelect' | 'includeRadioButton'>;
export const MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE: Record<
  ProcedureRunDataEntryMode,
  DataEntryModeConfig
> = {
  [ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER]: {
    graphic: 'worksheet',
    headerText: 'All conditions together',
    description: 'View and execute all conditions and replicates together in one worksheet',
    dataTestAttrs: {component: 'MultiBatchSelector', key: 'all-condition-replicates-together'},
  },
  [ProcedureRunDataEntryMode.INDIVIDUAL_CONDITION_REPLICATES]: {
    graphic: 'worksheets',
    headerText: 'One condition at a time',
    description: 'View and execute each condition replicate in separate worksheets',
    dataTestAttrs: {component: 'MultiBatchSelector', key: 'individual-condition-replicates'},
  },
};

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_TITLE,
    color: NEAR_BLACK,
    marginBottom: 12,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
});
